import { Icon } from './icons';
import React, { useEffect } from 'react';
import { LandingNav, LandingFooter, LandingNavProps, Spark } from './Landing';

/** Features page (route: /features). Each feature gets a CSS-drawn render + a write-up,
 *  alternating sides. Carries the same Get started + Login as the product page. */
export function FeaturesPage(nav: LandingNavProps) {
  const { onGetStarted, onSignIn } = nav;
  // Scroll reveals — same pattern as the landing page.
  useEffect(() => {
    const els = Array.from(document.querySelectorAll('.fx-land [data-reveal]'));
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => { if (e.isIntersecting) { e.target.classList.add('is-in'); io.unobserve(e.target); } });
    }, { threshold: 0.12 });
    els.forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, []);
  return (
    <div className="fx-land fx-land--features">
      <div className="fx-land-aurora" aria-hidden="true" />
      <LandingNav {...nav} />

      <section className="fx-feat-hero">
        <div className="fx-land-eyebrow">Features</div>
        <h1>Everything a modern team needs — <span className="fx-land-grad">in one place</span>.</h1>
        <p className="fx-land-sub">
          Messaging, calls, boards, calendars, and your AI agents — one focused workspace instead of
          a dozen tabs. Here's what's inside.
        </p>
        <div className="fx-land-cta-row fx-land-cta-center">
          <button type="button" className="fx-mk-btn" onClick={onGetStarted}>Get started free</button>
          <button type="button" className="fx-mk-ghost" onClick={onSignIn}>Sign in</button>
        </div>
      </section>

      {SECTIONS.map((s, i) => (
        <section className={`fx-feat-row${i % 2 ? ' is-rev' : ''}`} key={s.key} id={s.key} data-reveal>
          <div className="fx-feat-copy">
            <div className="fx-feat-eyebrow">{s.eyebrow}</div>
            <h2>{s.title}</h2>
            <p>{s.desc}</p>
            <ul className="fx-feat-bullets">
              {s.bullets.map((b) => <li key={b}>{b}</li>)}
            </ul>
          </div>
          <div className="fx-feat-render" aria-hidden="true">{s.render}</div>
        </section>
      ))}

      <section id="security" className="fx-land-band" data-reveal>
        <div className="fx-land-band-in">
          <div className="fx-land-band-ic"><Icon name="lock" size={22} /></div>
          <div>
            <h2>Secure by default — not as an afterthought.</h2>
            <p>
              Every account requires an authenticator-app second factor. Sign in with Google or a
              work/school Microsoft account. Each workspace's data is isolated, app &amp; bot tokens
              are scoped to exactly what an agent may do, and read receipts, profiles and presence
              are all under each person's own privacy controls.
            </p>
          </div>
        </div>
      </section>

      <section className="fx-land-final">
        <h2>Ready to bring it all together?</h2>
        <div className="fx-land-cta-row fx-land-cta-center">
          <button type="button" className="fx-mk-btn" onClick={onGetStarted}>Get started free</button>
          <button type="button" className="fx-mk-ghost" onClick={onSignIn}>Sign in</button>
        </div>
      </section>

      <LandingFooter onSignIn={onSignIn} onGetStarted={onGetStarted} />
    </div>
  );
}

// ── CSS-drawn feature renders (no external assets) ──────────────────────────────────────
function ChatRender() {
  return (
    <div className="fx-fr fx-fr-chat">
      <div className="fx-fr-row r1"><span className="fx-fr-av a1" /><span className="fx-fr-bub"><i className="w70" /><i className="w45" /></span></div>
      <div className="fx-fr-row is-me r2"><span className="fx-fr-bub me"><i className="w55" /></span></div>
      <div className="fx-fr-row r3"><span className="fx-fr-av bot"><Spark size={12} /></span><span className="fx-fr-bub bot"><i className="w80" /><i className="w60" /><i className="w35" /></span></div>
      <div className="fx-fr-row r4"><span className="fx-fr-av a2" /><span className="fx-fr-bub"><i className="w40" /></span></div>
    </div>
  );
}
function CallRender() {
  return (
    <div className="fx-fr fx-fr-call">
      <div className="fx-fr-vt g1"><span className="fx-fr-dot" /></div>
      <div className="fx-fr-vt g2 blur"><span className="fx-fr-face" /><em>Blur</em></div>
      <div className="fx-fr-vt g3"><span className="fx-fr-dot" /></div>
      <div className="fx-fr-vt g4"><span className="fx-fr-face" /></div>
    </div>
  );
}
function BoardRender() {
  const card = (cls = '') => (
    <div className={`fx-fr-cd${cls ? ` ${cls}` : ''}`}><i className="w75" /><i className="w45" /><span className="fx-fr-tag" /></div>
  );
  return (
    <div className="fx-fr fx-fr-board">
      <div className="fx-fr-col">
        <div className="fx-fr-col-h">To do</div>
        {card()}{card()}{card()}
      </div>
      <div className="fx-fr-col">
        <div className="fx-fr-col-h">Doing</div>
        {card()}{card('mv-out')}
      </div>
      <div className="fx-fr-col">
        <div className="fx-fr-col-h">Done</div>
        {card()}{card()}{card('mv-in')}
      </div>
    </div>
  );
}
function CalendarRender() {
  return (
    <div className="fx-fr fx-fr-cal">
      <div className="fx-fr-cal-h">{['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((d, i) => <span key={i}>{d}</span>)}</div>
      <div className="fx-fr-cal-grid">
        {Array.from({ length: 28 }).map((_, i) => (
          <div className={`fx-fr-cell${i === 9 ? ' today' : ''}`} key={i}>
            {i === 4 && <span className="fx-fr-ev e1" />}
            {i === 9 && <span className="fx-fr-ev e2" />}
            {i === 16 && <span className="fx-fr-ev e3" />}
            {i === 17 && <span className="fx-fr-ev e1" />}
          </div>
        ))}
      </div>
    </div>
  );
}
function ForumRender() {
  return (
    <div className="fx-fr fx-fr-forum">
      {[['w70', 12], ['w55', 4], ['w80', 27]].map(([w, n], i) => (
        <div className={`fx-fr-topic t${i + 1}`} key={i}>
          <span className="fx-fr-av a1" />
          <span className="fx-fr-topic-b"><i className={w as string} /><i className="w30 dim" /></span>
          <span className="fx-fr-reply"><Icon name="dm" size={11} /> {n}</span>
        </div>
      ))}
    </div>
  );
}

const SECTIONS: { key: string; eyebrow: string; title: string; desc: string; bullets: string[]; render: React.ReactNode }[] = [
  {
    key: 'chat', eyebrow: 'Messaging', title: 'Channels, DMs & agents in one thread',
    desc: 'Organize work in channels, go 1:1 or in groups, and keep momentum with threads, mentions, reactions and pins. Your FMAs are first-class members of every conversation.',
    bullets: ['Threads, mentions, reactions & pins', 'Agents you can @mention and hand work to', 'Read receipts with per-person privacy', 'Forum mode for titled, long-form topics'],
    render: <ChatRender />,
  },
  {
    key: 'calls', eyebrow: 'Calls & huddles', title: 'Face-to-face without leaving the room',
    desc: 'Start audio or video in a click. Pick your camera, mic and speaker live, blur or replace your background, and cut background noise — then record meeting notes that transcribe and summarize themselves.',
    bullets: ['Background blur & custom backgrounds', 'Live camera / mic / speaker switching', 'Noise reduction', 'Auto meeting notes: transcribe → summarize → post'],
    render: <CallRender />,
  },
  {
    key: 'boards', eyebrow: 'Boards & canvas', title: 'Plan and build on a shared canvas',
    desc: 'Every channel gets a living canvas — docs, tables, kanban boards and whiteboards the whole team can edit together, right where the conversation happens.',
    bullets: ['Kanban boards & data views', 'Collaborative docs & whiteboards', 'Tables, embeds and mindmaps', 'Live multiplayer editing'],
    render: <BoardRender />,
  },
  {
    key: 'calendar', eyebrow: 'Calendar & scheduling', title: 'Everyone\'s time, in one calendar',
    desc: 'Schedule events and calls, set recurrence and reminders, and see teammates\' local time and working hours. Connect Google or Outlook to bring your real calendar alongside.',
    bullets: ['Recurring events & smart reminders', 'Google & Microsoft calendar sync', 'Peer local time & working hours', 'Join meetings from inside the app'],
    render: <CalendarRender />,
  },
  {
    key: 'forum', eyebrow: 'Knowledge & forums', title: 'Discussions that don\'t get buried',
    desc: 'Flip any channel into Forum mode to turn it into a board of titled topics — each with its own threaded discussion — so decisions and long-form conversations stay findable.',
    bullets: ['Titled topics with their own threads', 'Most-active-first topic board', 'Auto-built knowledge base', 'Search across messages, people & files'],
    render: <ForumRender />,
  },
];
